import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { BaseContainer } from '../common/containers/base.abstract.container';

export enum HtmlParserNodeType {
    'span',
    'div',
    'p'
}

interface HtmlParsedStringPart {
    classes: string;
    message: string;
}

@Component({
    selector: 'sh-html-parser',
    templateUrl: './html-parser.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule]
})
export class ShHtmlParserComponent extends BaseContainer implements OnDestroy {
    private readonly WRAP_TEXT_CLASS: string = 'sh-wrap-text';
    private readonly LINE_BREAK_CLASS: string = 'sh-preserve-linebreaks';
    private readonly BOLD_CLASS: string = '-sh-bold';
    private readonly ITALICS_CLASS: string = '-sh-italics';

    public readonly HtmlParserNodeType: typeof HtmlParserNodeType = HtmlParserNodeType;

    public stringParts$: ReplaySubject<HtmlParsedStringPart[]> = new ReplaySubject(1);

    @Input() nodeType: HtmlParserNodeType = HtmlParserNodeType.span;
    @Input() set message(message: string) {
        if (message) {
            this.stringParts$.next(this.parseHtmlString(message));
        }
    }

    private parseHtmlString(message: string): HtmlParsedStringPart[] {
        const lineBreakPattern: RegExp = /<br ?\/?>/;
        let stringParts: HtmlParsedStringPart[] = [];

        let messagePart: string = message;
        let activeClasses: Record<string, number> = {
            [this.WRAP_TEXT_CLASS]: 1, // Always wrap text
            [this.LINE_BREAK_CLASS]: 0,
            [this.BOLD_CLASS]: 0,
            [this.ITALICS_CLASS]: 0
        };

        // Replace all occurences of line breaks with newlines
        if (lineBreakPattern.exec(message)) {
            messagePart = message.replace(new RegExp(lineBreakPattern, 'g'), '\n');
            activeClasses[this.LINE_BREAK_CLASS] = 1;
        } else if (message.includes('\n')) {
            activeClasses[this.LINE_BREAK_CLASS] = 1;
        }

        // Regex to find tags
        let match: RegExpExecArray | null = /<\/?[bi] ?>/.exec(messagePart);
        let timeOut: number = 0;

        while (match && timeOut < 100) {
            const tag: string = match[0];
            const potentialStringPart: HtmlParsedStringPart = {
                message: messagePart.substring(0, match.index),
                classes: this.getActiveClassesString(activeClasses)
            };

            let detectedClass: string;
            if (/<\/? ?b ?>/.exec(tag)) {
                // bold tag
                detectedClass = this.BOLD_CLASS;
                this.setActiveClasses(activeClasses, tag, this.BOLD_CLASS);
            } else {
                // Italics tag
                detectedClass = this.ITALICS_CLASS;
                this.setActiveClasses(activeClasses, tag, this.ITALICS_CLASS);
            }

            // Only split if there would be a change in styling
            if (this.shouldSplit(activeClasses, detectedClass)) {
                if (potentialStringPart.message.length > 0) {
                    stringParts.push(potentialStringPart);
                }
                messagePart = messagePart.substring(match.index + match[0].length);
            } else {
                messagePart = messagePart.replace(match[0], '');
            }

            match = /<\/? ?[bi] ?>/.exec(messagePart);
            timeOut += 1;
        }

        if (messagePart.length > 0) {
            stringParts.push({ message: messagePart, classes: this.getActiveClassesString(activeClasses) });
        }

        if (activeClasses[this.BOLD_CLASS] > 0 || activeClasses[this.ITALICS_CLASS] > 0) {
            console.warn('Malformed html string, parsed anyways.', message);
        }

        return stringParts;
    }

    private shouldSplit(activeClasses: Record<string, number>, cssClass: string): boolean {
        return activeClasses[cssClass] <= 1;
    }

    private setActiveClasses(activeClasses: Record<string, number>, tag: string, cssClass: string): void {
        if (tag.includes('/')) {
            activeClasses[cssClass] = Math.max(activeClasses[cssClass] - 1, 0);
            return;
        }

        activeClasses[cssClass] += 1;
    }

    private getActiveClassesString(activeClasses: Record<string, number>): string {
        return Object.entries(activeClasses)
            .filter(([cssClass, count]) => count > 0)
            .map(([cssClass, count]) => cssClass)
            .join(' ')
            .trim();
    }

    public ngOnDestroy(): void {
        this.stringParts$.complete();
    }
}
